import { ModalAction } from "@/components/common/ModalAction";
import EditIcon from "@mui/icons-material/Edit";
import { AxiosError, TAgentConnection, updateConnection } from "@/api";
import { useCallback, useState } from "react";
import {
  Button,
  Form,
  SelectInput,
  TextInput,
  BooleanInput,
  useNotify,
} from "react-admin";
import { Refresh } from "@mui/icons-material";
import { Box, Grid } from "@mui/material";

type Props = {
  connection: TAgentConnection;
  supplierId: string;
  onSuccess?: () => void;
};

export const EditConnectionModal = ({
  connection,
  supplierId,
  onSuccess,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const notify = useNotify();

  const onSubmit = useCallback(
    async (values: any, onClose: Function) => {
      setLoading(true);
      try {
        const updateData: {
          instructions?: string;
          privateInstructions?: string;
          config?: {
            accessToken?: string;
            model?: string;
            reasoning?: {
              effort?: "minimal" | "low" | "medium" | "high";
            };
            serviceTier?: "auto" | "default" | "flex" | "priority";
            autoCreateConsultantVectorStore?: boolean;
          };
        } = {};

        if (values.instructions !== undefined) {
          updateData.instructions = values.instructions;
        }
        if (values.privateInstructions !== undefined) {
          updateData.privateInstructions = values.privateInstructions;
        }
        if (
          values.accessToken !== undefined ||
          values.model !== undefined ||
          values.reasoningEffort !== undefined ||
          values.serviceTier !== undefined ||
          values.autoCreateConsultantVectorStore !== undefined
        ) {
          updateData.config = {};
          if (values.accessToken !== undefined) {
            updateData.config.accessToken = values.accessToken;
          }
          if (values.model !== undefined) {
            updateData.config.model = values.model;
          }
          if (values.reasoningEffort !== undefined) {
            updateData.config.reasoning = {
              effort: values.reasoningEffort,
            };
          }
          if (values.serviceTier !== undefined) {
            updateData.config.serviceTier = values.serviceTier;
          }
          if (values.autoCreateConsultantVectorStore !== undefined) {
            updateData.config.autoCreateConsultantVectorStore = values.autoCreateConsultantVectorStore;
          }
        }

        await updateConnection(connection.id, supplierId, updateData);

        notify("Conexión actualizada correctamente", {
          type: "success",
        });

        onSuccess?.();
        onClose();
      } catch (error) {
        let errorMessage = "Error al actualizar la conexión";

        if (error instanceof AxiosError) {
          errorMessage = error.response?.data?.message || errorMessage;
        }

        notify(errorMessage, {
          type: "error",
        });
      }

      setLoading(false);
    },
    [connection, supplierId, notify, onSuccess]
  );

  const formId = `edit-connection-form-${connection.id}`;

  return (
    <ModalAction
      buttonMode="icon"
      buttonIcon={<EditIcon />}
      buttonVariant="text"
      dialogMaxWidth="md"
      dialogTitle={`Editar Conexión: ${connection.name}`}
      dialogContent={(onClose) => (
        <Form
          id={formId}
          onSubmit={(values) => onSubmit(values, onClose)}
          defaultValues={{
            instructions: connection.instructions || "",
            privateInstructions: connection.privateInstructions || "",
            accessToken: connection.config?.accessToken || "",
            model: connection.config?.model || "",
            reasoningEffort: connection.config?.reasoning?.effort || "",
            serviceTier: connection.config?.serviceTier || "",
            autoCreateConsultantVectorStore: connection.config?.autoCreateConsultantVectorStore || false,
          }}
        >
          <Grid container spacing={2}>

            {connection.agent.key === "openai" && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    p: 2,
                    mt: 2,
                    mb: 1,
                    backgroundColor: "info.light",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "info.main",
                  }}
                >
                  <BooleanInput
                    source="autoCreateConsultantVectorStore"
                    label="Modo Consultoras (Vector Store Dinámico)"
                    helperText="Si se activa, el canal usará el teléfono del contacto para indexar y consultar un Vector Store único por usuario al recibir mensajes y documentos."
                    sx={{ mb: 0 }}
                  />
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextInput
                source="privateInstructions"
                label="Instrucciones Privadas"
                multiline
                rows={4}
                fullWidth
                helperText="Instrucciones privadas para el agente (no visibles para los usuarios)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextInput
                source="instructions"
                label="Instrucciones"
                multiline
                rows={4}
                fullWidth
                helperText="Instrucciones públicas para el agente (visibles para los usuarios)"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2, mt: 2 }}>
                <h3 style={{ marginTop: 0 }}>Configuración</h3>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextInput
                source="accessToken"
                label="Access Token"
                type="password"
                fullWidth
                helperText="Token de acceso de OpenAI"
              />
            </Grid>
            <Grid item xs={6}>
              <SelectInput
                source="model"
                label="Modelo"
                choices={[
                  { id: "gpt-5-mini", name: "gpt-5-mini" },
                  { id: "gpt-5", name: "gpt-5" },
                  { id: "gpt-4.1-mini", name: "gpt-4.1-mini" },
                  { id: "gpt-4.1", name: "gpt-4.1" },
                ]}
                fullWidth
                helperText="Modelo de OpenAI"
              />
            </Grid>
            <Grid item xs={6}>
              <SelectInput
                source="serviceTier"
                label="Service Tier"
                choices={[
                  { id: "auto", name: "Auto" },
                  { id: "default", name: "Default" },
                  { id: "flex", name: "Flex" },
                  { id: "priority", name: "Priority" },
                ]}
                fullWidth
                helperText="Nivel de servicio"
              />
            </Grid>
            <Grid item xs={12}>
              <SelectInput
                source="reasoningEffort"
                label="Reasoning Effort"
                choices={[
                  { id: "minimal", name: "Minimal" },
                  { id: "low", name: "Low" },
                  { id: "medium", name: "Medium" },
                  { id: "high", name: "High" },
                ]}
                fullWidth
                helperText="Nivel de esfuerzo de razonamiento"
              />
            </Grid>
          </Grid>
        </Form>
      )}
      dialogActions={
        <Button
          label="Guardar"
          type="submit"
          size="medium"
          variant="contained"
          form={formId}
          disabled={loading}
          startIcon={
            loading && <Refresh className={loading ? "rotate-icon" : ""} />
          }
        />
      }
    />
  );
};

